import { defineStore } from "pinia";
import axios from "@/utils/axios";
import { useSharedStore } from "@/stores/shared-store";
import { useAuthStore } from "@/stores/auth-store";
import { useExamFetchStore } from "@/stores/exam/exam-fetch-store";
import Swal from "sweetalert2";

export const useExamDataStore = defineStore("ExamData", {
  state: () => ({
    examAnswer: [],
    submission: null,
    submissionInfo: null,
    essayScore: [],
    lastSubmission: null,
    practicalScore: null,
  }),
  actions: {
    loadFromLocalStorage() {
      const saved = localStorage.getItem("exam_session");
      if (saved) {
        const data = JSON.parse(saved);
        this.submission = {
          examID: data.examID,
          submissionID: data.submissionID,
        };
        this.examAnswer = data.answers || [];
      }
    },
    saveToLocalStorage() {
      if (!this.submission) return;
      localStorage.setItem(
        "exam_session",
        JSON.stringify({
          examID: this.submission.examID,
          submissionID: this.submission.submissionID,
          answers: this.examAnswer,
        })
      );
    },
    clearSession() {
      localStorage.removeItem("exam_session");
      this.submission = null;
      this.examAnswer = [];
    },

    initAnswers() {
      const fetchStore = useExamFetchStore();
      if (fetchStore.examQuestions && fetchStore.examQuestions.length > 0) {
        this.examAnswer = fetchStore.examQuestions.map((q) => ({
          question: q.questionID,
          answer: null,
        }));
      }
      this.saveToLocalStorage();
    },
    setAnswer(quizId, answer) {
      const index = this.examAnswer.findIndex(
        (item) => String(item.question) === String(quizId)
      );

      if (index !== -1) {
        // Update jika sudah ada
        this.examAnswer[index].answer = answer;
      } else {
        // Tambahkan jika belum ada di list agar jawaban tidak hilang
        this.examAnswer.push({
          question: quizId,
          answer: answer,
        });
      }
      this.saveToLocalStorage();
    },

    async fetchSubmission(submissionID) {
      const sharedStore = useSharedStore();
      const authStore = useAuthStore();

      sharedStore.startRequest();
      try {
        const response = await axios.get(
          `${authStore.Url}/api/exam/exam-submissions/${submissionID}/`
        );
        this.submissionInfo = response.data; // simpan data submission

        return this.submission;
      } catch (error) {
        console.error("Gagal membuat submission:", error);
        throw error;
      } finally {
        sharedStore.endRequest();
      }
    },

    async getLastSubmission(examID, studentID) {
      const sharedStore = useSharedStore();
      const authStore = useAuthStore();

      sharedStore.startRequest();
      try {
        const { data } = await axios.get(
          `${authStore.Url}/api/exam/exam-submissions/last/`,
          {
            params: {
              examID: examID,
              studentID: studentID,
            },
          }
        );

        // simpan hasil ke state biar bisa dipakai ulang
        this.lastSubmission = data;

        return data;
      } catch (error) {
        console.error("Gagal mengambil last submission:", error);
        return null;
      } finally {
        sharedStore.endRequest();
      }
    },

    async createSubmission(examID, studentID) {
      const sharedStore = useSharedStore();
      const authStore = useAuthStore();

      sharedStore.startRequest();
      try {
        const response = await axios.post(
          `${authStore.Url}/api/exam/exam-submissions/`,
          {
            examID: examID,
            studentID: studentID,
          }
        );

        this.submission = response.data; // simpan data submission

        console.log("Submission created:", this.submission);

        return this.submission;
      } catch (error) {
        console.error("Gagal membuat submission:", error);
        throw error;
      } finally {
        sharedStore.endRequest();
      }
    },
    async submitAnswers() {
      const sharedStore = useSharedStore();
      const authStore = useAuthStore();

      sharedStore.startRequest();
      try {
        const response = await axios.patch(
          `${authStore.Url}/api/exam/exam-submissions/${this.submission.submissionID}/submit-answers/`,
          {
            answers: this.examAnswer,
          }
        );

        console.log("Submit response:", response.data);

        return response.data;
      } catch (error) {
        console.error("Gagal submit jawaban:", error);
        throw error;
      } finally {
        sharedStore.endRequest();
      }
    },
    async uploadVidio(submissionID, vidio) {
      const sharedStore = useSharedStore();
      const authStore = useAuthStore();

      sharedStore.startRequest();
      try {
        const formData = new FormData();
        formData.append("examVideoPath", vidio);

        const response = await axios.patch(
          `${authStore.Url}/api/exam/exam-submissions/${submissionID}/upload-video/`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        sharedStore.showSuccess("Video ujian berhasil diupload ✅");
        return response.data;
      } catch (error) {
        sharedStore.showError(error.response?.data || error.message);
        console.log(error);
      } finally {
        sharedStore.endRequest();
      }
    },

    // ==================== UNTUK TEACHER SCORING ====================
    initScoring() {
      const fetchStore = useExamFetchStore();
      if (
        fetchStore.answerListBySubmission &&
        fetchStore.answerListBySubmission.length > 0
      ) {
        const essayQuestions = fetchStore.answerListBySubmission.filter(
          (q) => q.questionType === "Essay"
        );
        this.essayScore = essayQuestions.map((ans) => ({
          question: ans.question, // ID pertanyaan
          score: ans.score ? Number(ans.score) : null, // default null jika belum ada nilai
        }));
      }
      this.saveScoring();
    },

    loadScoring() {
      const saved = localStorage.getItem("scoring_session");
      if (saved) {
        const data = JSON.parse(saved);
        this.essayScore = data.scores || [];
      }
    },

    saveScoring() {
      localStorage.setItem(
        "scoring_session",
        JSON.stringify({
          scores: this.essayScore,
        })
      );
    },

    clearScoring() {
      localStorage.removeItem("scoring_session");
      this.essayScore = [];
    },

    setScore(questionId, score) {
      const index = this.essayScore.findIndex(
        (item) => item.question === questionId
      );

      if (index !== -1) {
        // Update jika sudah ada
        this.essayScore[index].score = score;
        this.saveScoring();
      }
    },
    async gradeEssay(submissionID) {
      const sharedStore = useSharedStore();
      const authStore = useAuthStore();

      sharedStore.startRequest();
      try {
        const { data } = await axios.patch(
          `${authStore.Url}/api/exam/exam-submissions/${submissionID}/grade-essay/`,
          {
            essays: this.essayScore,
          }
        );
        return data;
      } catch (error) {
        console.error("Error grading essay:", error.response?.data || error);
        throw error;
      } finally {
        sharedStore.endRequest();
      }
    },
    async gradePractical(submissionID) {
      const sharedStore = useSharedStore();
      const authStore = useAuthStore();

      sharedStore.startRequest();
      try {
        const { data } = await axios.patch(
          `${authStore.Url}/api/exam/exam-submissions/${submissionID}/grade-practical/`,
          {
            practicalScore: this.practicalScore,
          }
        );
        this.practicalScore = null;
        return data;
      } catch (error) {
        console.error("Error grading essay:", error.response?.data || error);
        throw error;
      } finally {
        sharedStore.endRequest();
      }
    },
    async uploadCertificate(pdfBlob, courseId, studentID = null) {
      const authStore = useAuthStore();
      const sharedStore = useSharedStore();

      sharedStore.startRequest();
      try {
        const formData = new FormData();
        const targetStudentID = studentID || authStore.currentUser?.id;
        formData.append("studentID", targetStudentID);
        formData.append("courseID", courseId);
        formData.append("pdfFile", pdfBlob, `certificate-${Date.now()}.pdf`);

        await axios.post(
          `${authStore.Url}/api/exam/certificates/claim/`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        // Upload sukses berjalan di latar belakang tanpa swal berhasil
      } catch (error) {
        if (error.response && error.response.status === 409) {
          console.warn(
            "Certificate info:",
            error.response.data?.message || "Certificate sudah pernah di-claim"
          );
        } else {
          console.error("Gagal menyimpan certificate:", error);
          Swal.fire({
            title: "Error",
            text:
              error.response?.data?.error ||
              "Gagal menyimpan certificate ke database",
            icon: "error",
          });
        }
      } finally {
        sharedStore.endRequest();
      }
    },
    async downloadCertificate(courseId, studentId = null, certId = null) {
      const authStore = useAuthStore();
      const targetStudentID = studentId || authStore.currentUser?.id;

      const url = `${authStore.Url}/api/exam/certificates/download/`;
      const params = {};
      if (certId) {
        params.certificate_id = certId;
      } else {
        if (courseId) params.course_id = courseId;
        if (targetStudentID) params.student_id = targetStudentID;
      }

      const response = await axios.get(url, {
        params,
        responseType: "blob",
      });
      return response.data;
    },
  },
});
